import React, { useMemo, useState } from 'react';
import { DonorTypes, Group, GroupsPayload } from "./types";
import GroupManager from "./GroupManager";
import { BASE_URL, FUND_DESIGNATIONS } from "./constants";
import { DonorTypeSelector, GroupSelector, FundSelector, MembershipCheckbox, DonateButton } from "./components";

interface FormProps {
    groups: GroupsPayload;
    isProd: boolean;
}

const Form: React.FC<FormProps> = ({ groups, isProd }) => {
    const [isMember, setIsMember] = useState<boolean>(false);
    const [donorType, setDonorType] = useState<DonorTypes>(DonorTypes.INDIVIDUAL);
    const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
    const [fundDesignation, setFundDesignation] = useState<string>(FUND_DESIGNATIONS[0].value);

    const groupManager = useMemo(() => new GroupManager(groups), [groups]);
    const groupOptions = useMemo(() => {
        const options = groupManager.getGroupsForType(donorType);
        return options.map(group => ({
            value: group,
            label: GroupManager.getOptionText(group)
        }));
    }, [donorType, groupManager]);

    const canSubmit = useMemo<boolean>(() => (
        isMember && !!donorType && (donorType === DonorTypes.INDIVIDUAL || !!selectedGroup) && !!fundDesignation
    ), [isMember, donorType, selectedGroup, fundDesignation]);

    const handleDonorTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDonorType(event.target.value as DonorTypes);
        setSelectedGroup(null);
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) return;

        const params = new URLSearchParams({
            designationId: fundDesignation,
            fundraiseupLivemode: isProd ? 'yes' : 'no',
        });
        if (donorType) {
            params.set('donorType', donorType);
        }
        if (selectedGroup) {
            params.set('groupNumber', selectedGroup.group_id);
        }

        window.location.href = `${BASE_URL}?${params.toString()}`;
    };

    const isGroup = donorType !== DonorTypes.INDIVIDUAL;

    return (
        <div className="max-w-lg mx-auto bg-white p-0 sm:p-4 rounded-lg sm:shadow-md text-left font-sans">
            <h3 className="text-2xl font-bold mb-2">7th Tradition Contribution</h3>
            <form onSubmit={handleSubmit} className="space-y-6 text-base">
                <DonorTypeSelector
                    donorType={donorType}
                    onChange={handleDonorTypeChange}
                />

                {isGroup && (
                    <GroupSelector
                        donorType={donorType}
                        selectedGroup={selectedGroup}
                        groupOptions={groupOptions}
                        onGroupChange={setSelectedGroup}
                    />
                )}

                <FundSelector
                    fund={fundDesignation}
                    options={FUND_DESIGNATIONS}
                    onChange={setFundDesignation}
                />

                <MembershipCheckbox
                    isMember={isMember}
                    onChange={setIsMember}
                />

                <DonateButton canSubmit={canSubmit}/>
            </form>
        </div>
    );
};

export default Form;